import { Link, useLocation, useNavigate } from "react-router-dom";
import { BarChart3, Library, Layers, ClipboardList, ShieldCheck, LogOut } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const NavItem = ({ to, icon: Icon, label, testid }) => {
  const { pathname } = useLocation();
  const active = pathname === to || (to !== "/" && pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={testid}
      className={cn(
        "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors",
        active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
      )}
    >
      <Icon className="h-4 w-4" />
      <span className="hidden sm:inline">{label}</span>
    </Link>
  );
};

const navForRole = (role) => {
  if (role === "ADMIN") return [{ to: "/admin", icon: ShieldCheck, label: "Admin", testid: "nav-admin" }];
  if (role === "EXPERT") return [{ to: "/teacher", icon: ClipboardList, label: "Review", testid: "nav-teacher" }];
  return [
    { to: "/practice", icon: Library, label: "Practice", testid: "nav-practice" },
    { to: "/chapters", icon: Layers, label: "Chapters", testid: "nav-chapters" },
    { to: "/stats", icon: BarChart3, label: "Stats", testid: "nav-stats" },
  ];
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const authed = user && typeof user === "object";
  const items = authed ? navForRole(user.role) : [];

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
            <BrandMark className="h-9" />
            <span className="font-heading text-lg font-bold tracking-tight">Z2Infinity</span>
          </Link>

          <nav className="flex items-center gap-1.5">
            {items.map((it) => <NavItem key={it.to} {...it} />)}
            <div className="ml-1"><ThemeToggle /></div>
            {authed ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu" className="ml-1 flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-bold uppercase text-foreground">
                    {user.picture ? <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" /> : (user.name || user.email)[0]}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate">
                    <div className="font-medium">{user.name}</div>
                    <div className="truncate text-xs font-normal text-muted-foreground">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="text-red-500">
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="ml-1 flex items-center gap-2">
                <Button asChild variant="ghost" size="sm" data-testid="nav-login" className="rounded-full"><Link to="/login">Sign in</Link></Button>
                <Button asChild size="sm" data-testid="nav-register" className="rounded-full"><Link to="/register">Get started</Link></Button>
              </div>
            )}
          </nav>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
};
